import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Alert,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as DocumentIcon,
  Photo as PhotoIcon,
  Person as SelfieIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { KYCDocumentType, KYCDocument } from '../../types/kyc';
import { uploadService } from '../../services/uploadService';
import { kycService } from '../../services/kycService';
import { useThemeMode } from '../../contexts/ThemeContext';

interface DocumentSlot {
  type: KYCDocumentType;
  label: string;
  description: string;
  icon: React.ReactNode;
  accept: string;
}

const documentSlots: DocumentSlot[] = [
  {
    type: 'id_document',
    label: 'ID Document',
    description: 'Passport, driver\'s license, or national ID',
    icon: <DocumentIcon />,
    accept: '.pdf,.jpg,.jpeg,.png',
  },
  {
    type: 'proof_of_address',
    label: 'Proof of Address',
    description: 'Utility bill or bank statement (less than 3 months old)',
    icon: <PhotoIcon />,
    accept: '.pdf,.jpg,.jpeg,.png',
  },
  {
    type: 'selfie',
    label: 'Selfie with ID',
    description: 'Photo of yourself holding your ID document',
    icon: <SelfieIcon />,
    accept: '.jpg,.jpeg,.png',
  },
];

interface KYCDocumentUploadProps {
  userId: string;
  existingDocuments?: KYCDocument[];
  onDocumentUploaded?: (document: KYCDocument) => void;
  onDocumentRemoved?: (documentType: KYCDocumentType) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

interface UploadState {
  [key: string]: {
    uploading: boolean;
    progress: number;
    error: string | null;
    document: KYCDocument | null;
  };
}

const KYCDocumentUpload: React.FC<KYCDocumentUploadProps> = ({
  userId,
  existingDocuments = [],
  onDocumentUploaded,
  onDocumentRemoved,
  disabled = false,
  readOnly = false,
}) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // Initialize upload state with existing documents
  const initializeState = (): UploadState => {
    const state: UploadState = {};
    documentSlots.forEach((slot) => {
      const existing = existingDocuments.find((d) => d.document_type === slot.type);
      state[slot.type] = {
        uploading: false,
        progress: 0,
        error: null,
        document: existing || null,
      };
    });
    return state;
  };

  const [uploadState, setUploadState] = useState<UploadState>(initializeState);

  const handleFileSelect = async (type: KYCDocumentType, file: File) => {
    // Validate file
    const validationError = uploadService.validateFile(file);
    if (validationError) {
      setUploadState((prev) => ({
        ...prev,
        [type]: { ...prev[type], error: validationError.message },
      }));
      return;
    }

    // Start upload
    setUploadState((prev) => ({
      ...prev,
      [type]: { ...prev[type], uploading: true, progress: 10, error: null },
    }));

    try {
      // Upload file to storage
      setUploadState((prev) => ({
        ...prev,
        [type]: { ...prev[type], progress: 30 },
      }));

      const uploadResponse = await uploadService.uploadFile(file);

      setUploadState((prev) => ({
        ...prev,
        [type]: { ...prev[type], progress: 60 },
      }));

      // Register document with KYC
      const document = await kycService.uploadDocument(userId, {
        document_type: type,
        file_name: uploadResponse.file_name,
        file_url: uploadResponse.file_url,
        file_size: uploadResponse.file_size,
        mime_type: uploadResponse.mime_type,
      });

      setUploadState((prev) => ({
        ...prev,
        [type]: {
          uploading: false,
          progress: 100,
          error: null,
          document,
        },
      }));

      onDocumentUploaded?.(document);
    } catch (error: any) {
      setUploadState((prev) => ({
        ...prev,
        [type]: {
          ...prev[type],
          uploading: false,
          progress: 0,
          error: error.message || 'Upload failed',
        },
      }));
    }
  };

  const handleInputChange = (type: KYCDocumentType) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(type, file);
    }
    // Reset input value so the same file can be selected again
    event.target.value = '';
  };

  const handleRemove = (type: KYCDocumentType) => {
    setUploadState((prev) => ({
      ...prev,
      [type]: {
        uploading: false,
        progress: 0,
        error: null,
        document: null,
      },
    }));
    onDocumentRemoved?.(type);
  };

  const handleViewDocument = (document: KYCDocument) => {
    const url = uploadService.getFileUrl(document.file_url);
    window.open(url, '_blank');
  };

  const renderDocumentSlot = (slot: DocumentSlot) => {
    const state = uploadState[slot.type];
    const hasDocument = !!state.document;

    return (
      <Grid item xs={12} md={4} key={slot.type}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card
            sx={{
              height: '100%',
              background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${
                hasDocument
                  ? isDark
                    ? 'rgba(34, 197, 94, 0.3)'
                    : 'rgba(34, 197, 94, 0.5)'
                  : isDark
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(0, 0, 0, 0.08)'
              }`,
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: hasDocument
                  ? isDark
                    ? 'rgba(34, 197, 94, 0.5)'
                    : 'rgba(34, 197, 94, 0.7)'
                  : '#3B82F6',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: hasDocument
                      ? 'linear-gradient(135deg, #22C55E, #10B981)'
                      : 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                    color: 'white',
                    mr: 2,
                  }}
                >
                  {hasDocument ? <CheckIcon /> : slot.icon}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {slot.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {slot.description}
                  </Typography>
                </Box>
              </Box>

              {/* Error Alert */}
              <AnimatePresence>
                {state.error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => {
                      setUploadState((prev) => ({
                        ...prev,
                        [slot.type]: { ...prev[slot.type], error: null },
                      }));
                    }}>
                      {state.error}
                    </Alert>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Upload Progress */}
              {state.uploading && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={state.progress}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, #1E40AF, #3B82F6)',
                        borderRadius: 3,
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Uploading... {state.progress}%
                  </Typography>
                </Box>
              )}

              {/* Document Info or Upload Button */}
              {hasDocument && state.document ? (
                <Box>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {state.document.file_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {uploadService.formatFileSize(state.document.file_size)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Document">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDocument(state.document!)}
                        sx={{ color: '#3B82F6' }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    {!readOnly && !disabled && (
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          onClick={() => handleRemove(slot.type)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              ) : (
                !readOnly && (
                  <>
                    <input
                      type="file"
                      accept={slot.accept}
                      style={{ display: 'none' }}
                      ref={(el) => (fileInputRefs.current[slot.type] = el)}
                      onChange={handleInputChange(slot.type)}
                      disabled={disabled || state.uploading}
                    />
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<UploadIcon />}
                      onClick={() => fileInputRefs.current[slot.type]?.click()}
                      disabled={disabled || state.uploading}
                      sx={{
                        borderRadius: 2,
                        borderColor: isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.5)',
                        color: '#3B82F6',
                        '&:hover': {
                          borderColor: '#3B82F6',
                          background: 'rgba(59, 130, 246, 0.1)',
                        },
                      }}
                    >
                      {state.uploading ? 'Uploading...' : 'Upload File'}
                    </Button>
                  </>
                )
              )}
            </CardContent>
          </Card>
        </motion.div>
      </Grid>
    );
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {documentSlots.map(renderDocumentSlot)}
      </Grid>
    </Box>
  );
};

export default KYCDocumentUpload;
